<template>
  <div class="mt-3 pt-3 border-t border-gray-300">
    <button
      type="button"
      class="text-xs text-gray-600 hover:text-dark-primary"
      @click="toggle"
    >{{ open ? '▼' : '▶' }} History ({{ transitions.length || '...' }})</button>

    <div v-if="open" class="mt-2">
      <div v-if="loading" class="text-xs text-gray-500 p-2">Loading history...</div>
      <div v-else-if="error" class="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{{ error }}</div>
      <div v-else-if="transitions.length === 0" class="text-xs text-gray-500 p-2">
        No history yet.
      </div>
      <table v-else class="table-fixed w-full text-xs">
        <colgroup>
          <col class="w-40" />
          <col class="w-40" />
          <col class="w-48" />
          <col class="w-56" />
          <col />
        </colgroup>
        <thead class="bg-gray-100">
          <tr>
            <th class="text-left px-2 py-1">When</th>
            <th class="text-left px-2 py-1">Actor</th>
            <th class="text-left px-2 py-1">Action</th>
            <th class="text-left px-2 py-1">Status change</th>
            <th class="text-left px-2 py-1">Reason / Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in transitions" :key="t.id" class="border-t border-gray-200 align-top">
            <td class="px-2 py-1 whitespace-nowrap">{{ formatDateTime(t.created_at) }}</td>
            <td class="px-2 py-1 break-words">{{ t.actor ? t.actor.full_name : '—' }}</td>
            <td class="px-2 py-1 whitespace-nowrap">{{ actionLabel(t.action) }}</td>
            <td class="px-2 py-1 text-gray-700">
              <span v-if="t.from_status && t.to_status && t.from_status !== t.to_status">
                {{ t.from_status }} → {{ t.to_status }}
              </span>
              <span v-else-if="t.to_status">→ {{ t.to_status }}</span>
              <span v-else class="text-gray-400">—</span>
            </td>
            <td class="px-2 py-1 break-words">
              <div v-if="t.reason" class="whitespace-pre-wrap">{{ t.reason }}</div>
              <div v-if="t.metadata" class="text-gray-500 text-[0.7rem] mt-0.5">
                {{ formatMetadata(t.metadata) }}
              </div>
              <span v-if="!t.reason && !t.metadata" class="text-gray-400">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default { name: "expense-history" };
</script>

<script setup>
import { ref, defineProps } from "vue";
import { useStore } from "vuex";
import { getExpenseHistory } from "@/services/Expense.js";

const props = defineProps({
  expenseId: { type: [Number, String], required: true },
});

const store = useStore();
const open = ref(false);
const loading = ref(false);
const error = ref("");
const transitions = ref([]);

const ACTION_LABELS = {
  submit: "Submit",
  recall: "Recall",
  manager_approve: "Manager approved",
  manager_reject: "Manager rejected",
  accounting_approve: "Accounting approved",
  accounting_reject: "Accounting rejected",
  mark_submitter_reimbursed: "Marked reimbursed",
  mark_client_invoiced: "Marked client invoiced",
  mark_client_paid: "Marked client paid",
  mark_bank_reconciled: "Marked bank reconciled",
  close: "Closed",
  admin_override_status: "Admin: override status",
  admin_override_field: "Admin: override field",
  admin_reassign_manager: "Admin: reassign manager",
  admin_soft_delete: "Admin: soft delete",
  admin_restore: "Admin: restore",
  admin_unmark_reimbursed: "Admin: unmark reimbursed",
  admin_unmark_client_paid: "Admin: unmark client paid",
  admin_unmark_bank_reconciled: "Admin: unmark bank reconciled",
};

function actionLabel(code) {
  return ACTION_LABELS[code] || code;
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
}

function formatMetadata(meta) {
  if (!meta) return "";
  try {
    if (typeof meta === "string") return meta;
    return Object.entries(meta)
      .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
      .join(" · ");
  } catch (e) {
    return String(meta);
  }
}

async function load() {
  loading.value = true;
  error.value = "";
  const token = store.state.auth.token;
  try {
    const data = await getExpenseHistory(token, props.expenseId);
    transitions.value = data.transitions || [];
  } catch (err) {
    error.value = (err.response && err.response.data && JSON.stringify(err.response.data))
      || err.message || "Failed to load history.";
  }
  loading.value = false;
}

function toggle() {
  open.value = !open.value;
  if (open.value && transitions.value.length === 0 && !loading.value && !error.value) {
    load();
  }
}
</script>
